'use client'

import { useEffect, useState } from 'react'
import { Bluetooth, Download, RefreshCw, ShieldCheck, Smartphone, Usb } from 'lucide-react'
import { Button } from '@/components/ui/button'

type GuideVariant = 'beginner' | 'reference'

type MobileRelease = {
  version?: string | null
  apk_url?: string | null
}

type MobileStep = {
  number: string
  title: string
  label: string
  image: string
  alt: string
  body: string[]
  points: string[]
  icon: typeof Smartphone
}

const beginnerSteps: MobileStep[] = [
  {
    number: '01',
    title: 'Start with a clean workspace',
    label: 'Welcome',
    image: '/mobile/01-welcome.png',
    alt: 'NewChirp Mobile welcome screen on Android',
    body: [
      'NewChirp Mobile starts fresh every time. It does not silently reload channels or settings from an earlier session, so you always know what you are looking at.',
      'Choose Connect a radio for Bluetooth or USB, import a CHIRP CSV from desktop, build a blank list, or try the built-in 22-channel GMRS starter.',
    ],
    points: [
      'Tap Connect when you are ready to program a radio.',
      'Tap the information icon for app updates and connection help.',
      'Nothing is written to a radio until you read it first.',
    ],
    icon: Smartphone,
  },
  {
    number: '02',
    title: 'Review the channel library',
    label: 'Channels',
    image: '/mobile/02-channels.png',
    alt: 'NewChirp Mobile channel library showing 22 GMRS channels',
    body: [
      'The channel library summarizes programmed channels, repeaters, and validation issues. Search by channel name, number, or frequency.',
      'You can prepare channels before connecting a radio. The yellow status banner reminds you that writing remains locked until a compatible radio has been read.',
    ],
    points: [
      'Use the channel count to confirm the list you expect.',
      'Search makes long channel plans easy to navigate by thumb.',
      'Tap + Channel to add a new memory.',
    ],
    icon: Smartphone,
  },
  {
    number: '03',
    title: 'Edit the important details',
    label: 'Editor',
    image: '/mobile/03-channel-editor.png',
    alt: 'NewChirp Mobile channel editor with frequency and tone fields',
    body: [
      'Tap any channel to edit its name, receive frequency, duplex mode, offset, bandwidth, power, scan behavior, tone, and notes.',
      'Changes are made locally first. Saving closes the editor and keeps the updated channel in the current workspace until you intentionally write it to the radio.',
    ],
    points: [
      'Use Simplex for direct radio-to-radio channels.',
      'Use + or − offset for repeater channels.',
      'Tap Save channel before leaving the screen.',
    ],
    icon: Smartphone,
  },
  {
    number: '04',
    title: 'Connect over Bluetooth',
    label: 'Bluetooth',
    image: '/mobile/04-bluetooth.png',
    alt: 'NewChirp Mobile Bluetooth connection and radio selection screen',
    body: [
      'Enable Bluetooth programming on the radio, or connect a supported radio to a TIDRADIO TD-BL-1 Bluetooth programmer dongle. Choose the matching radio model and tap Scan.',
      'Select the radio or TD-BL-1 adapter and tap Connect. The advertised name may be generic, such as “walkie-talkie”; use the pencil to give it a name you recognize inside NewChirp.',
    ],
    points: [
      'Bluetooth programming requires a compatible BLE radio or TD-BL-1 dongle.',
      'Tap Read radio after connecting to download the current memory.',
      'Do not disconnect while a transfer is in progress.',
    ],
    icon: Bluetooth,
  },
  {
    number: '05',
    title: 'Or connect with a USB cable',
    label: 'USB / OTG',
    image: '/mobile/05-usb.png',
    alt: 'NewChirp Mobile USB cable and radio catalog screen',
    body: [
      'On Android, switch to USB cable, connect a supported USB serial programming cable with any needed USB-C adapter, and tap Refresh.',
      'The searchable catalog contains Bluetooth and USB-cable programmable models. Pick the exact family for your radio before connecting.',
    ],
    points: [
      'USB programming is available on Android, not iPhone.',
      'Approve Android’s USB access prompt for the cable.',
      'Keep the radio powered on during read and write operations.',
    ],
    icon: Usb,
  },
  {
    number: '06',
    title: 'Stay current safely',
    label: 'Updates',
    image: '/mobile/06-updates.png',
    alt: 'NewChirp Mobile app updates card on Android',
    body: [
      'Open the information icon to see the installed Android version. NewChirp checks for updates at startup and whenever the app returns to the foreground.',
      'Tap Check for updates for a manual check. The app verifies the download checksum, package name, version, and signing certificate before opening Android’s installer.',
    ],
    points: [
      'Android may ask permission to install updates from NewChirp.',
      'The app never installs an update silently.',
      'Use the same release signing key for every future build.',
    ],
    icon: RefreshCw,
  },
]

const referenceSteps: MobileStep[] = [
  {
    number: '01',
    title: 'Launch into a fresh, predictable session',
    label: 'Session model',
    image: '/mobile/01-welcome.png',
    alt: 'NewChirp Mobile welcome screen on Android',
    body: [
      'NewChirp Mobile deliberately clears the channel workspace, radio image, and settings block at launch. Persistent app state is limited to preferences such as the selected model and Bluetooth display aliases.',
      'This prevents a previous radio’s data from being mistaken for the device currently connected.',
    ],
    points: [
      'Fresh launch means an empty channel list by design.',
      'Use CSV import for a saved desktop channel plan.',
      'Use the built-in GMRS starter for a known 22-channel template.',
    ],
    icon: Smartphone,
  },
  {
    number: '02',
    title: 'Understand the channel workspace',
    label: 'Library state',
    image: '/mobile/02-channels.png',
    alt: 'NewChirp Mobile channel library dashboard',
    body: [
      'The library reports total active channels, repeater channels, and validation issues. Search filters the visible list without changing the workspace.',
      'The write state is tied to a successful read from the current connection and selected model. A prebuilt or imported list is editable immediately but cannot be uploaded until that requirement is satisfied.',
    ],
    points: [
      'Read-before-write protects against writing to the wrong radio.',
      'Clearing a channel marks it for removal on the next write.',
      'Edit actions remain local until the radio transfer begins.',
    ],
    icon: ShieldCheck,
  },
  {
    number: '03',
    title: 'Inspect every transmitted field',
    label: 'Channel data',
    image: '/mobile/03-channel-editor.png',
    alt: 'NewChirp Mobile channel memory editor',
    body: [
      'Each memory exposes the fields used by the selected radio driver: receive frequency, duplex and offset, mode, power, scan skip, analog tone, and notes.',
      'The editor uses the active radio profile’s limits. Extended CHIRP-engine profiles delegate model-specific validation to their bundled driver.',
    ],
    points: [
      'Tone mode determines which transmit and receive tone fields apply.',
      'Split mode uses the offset field as a transmit frequency.',
      'Long names are filtered by the driver during a CHIRP write.',
    ],
    icon: Smartphone,
  },
  {
    number: '04',
    title: 'BLE transport and radio identity',
    label: 'BLE + TD-BL-1',
    image: '/mobile/04-bluetooth.png',
    alt: 'NewChirp Mobile Bluetooth radio discovery screen',
    body: [
      'The native Bluetooth path scans for the HM-10-style programming service and uses GATT writes and notifications as a transparent serial channel. It also recognizes the TIDRADIO TD-BL-1 programmer dongle and completes its ODMaster-compatible AES challenge before transferring radio data.',
      'Select the radio profile before scanning. The advertised BLE name is controlled by the radio or dongle; NewChirp aliases are local display labels and are not written back to the device.',
    ],
    points: [
      'Use the exact model family when connecting through a TD-BL-1 dongle.',
      'Read the radio before enabling a write.',
      'Keep the radio awake and nearby for the full transfer.',
    ],
    icon: Bluetooth,
  },
  {
    number: '05',
    title: 'Android USB serial path',
    label: 'USB OTG',
    image: '/mobile/05-usb.png',
    alt: 'NewChirp Mobile Android USB serial cable screen',
    body: [
      'Android USB mode discovers supported serial bridges and bridges the selected cable into the native Dart protocol or embedded CHIRP driver.',
      'Driver-specific baud rate, flow control, RTS, and DTR settings are applied by the selected radio profile. iOS does not expose generic USB serial devices to apps.',
    ],
    points: [
      'Typical bridges include CH340, CP210x, FTDI, Prolific, and CDC.',
      'Use a USB OTG or USB-C adapter when the cable has USB-A.',
      'Refresh after attaching the cable or granting USB access.',
    ],
    icon: Usb,
  },
  {
    number: '06',
    title: 'Signed over-the-air beta updates',
    label: 'Update verification',
    image: '/mobile/06-updates.png',
    alt: 'NewChirp Mobile signed update checking screen',
    body: [
      'The Android client reads a versioned JSON manifest from newchirp.net. A newer build is downloaded over HTTPS and checked before Android sees it.',
      'Verification includes SHA-256, package identity, version and build number, and the release signing certificate. The installer still requires explicit user confirmation.',
    ],
    points: [
      'Builds are numbered so same-version security or bug fixes can ship.',
      'Checks are cached briefly and also available manually.',
      'A checksum or signer mismatch is rejected before installation.',
    ],
    icon: RefreshCw,
  },
]

export function MobileGuide({ variant }: { variant: GuideVariant }) {
  const beginner = variant === 'beginner'
  const steps = beginner ? beginnerSteps : referenceSteps
  const [mobileRelease, setMobileRelease] = useState<MobileRelease>({})

  useEffect(() => {
    let cancelled = false
    fetch('/api/releases/mobile/latest', { cache: 'no-store' })
      .then((response) => (response.ok ? response.json() : null))
      .then((release: MobileRelease | null) => {
        if (!cancelled && release) setMobileRelease(release)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])

  const version = mobileRelease.version?.trim()
  const downloadUrl = mobileRelease.apk_url || '/#download'

  return (
    <section id="mobile" className="scroll-mt-20 border-b border-border/60 py-12 md:py-16">
      <div className="mx-auto max-w-6xl">
        <div className="max-w-3xl">
          <span className="font-mono text-sm text-primary">
            // {beginner ? 'Android quick start' : 'mobile technical guide'}
          </span>
          <h2 className="mt-3 text-balance text-3xl font-bold tracking-tight sm:text-4xl">
            {beginner
              ? 'Take NewChirp with you'
              : 'NewChirp Mobile: Bluetooth, USB, and update workflow'}
          </h2>
          <p className="mt-4 text-pretty leading-relaxed text-muted-foreground">
            {beginner
              ? 'The Android beta puts the channel editor on the radio you already carry. Follow the same six screens from a fresh install to your first successful radio transfer.'
              : 'This reference pairs the current Android interface with the transport, safety, and update behavior behind each screen.'}
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Button nativeButton={false} render={<a href={downloadUrl} />}>
              <Download className="h-4 w-4" />
              Download Android beta
            </Button>
            <Button
              variant="outline"
              nativeButton={false}
              render={<a href="/#download" />}
            >
              See all platforms
            </Button>
          </div>
          <p className="mt-3 font-mono text-xs text-muted-foreground">
            Android 9+ · Bluetooth LE + USB OTG ·{' '}
            {version ? `Version ${version}` : 'Latest release'}
          </p>
        </div>

        <div className="mt-14 flex flex-col gap-16 md:gap-24">
          {steps.map((step, index) => {
            const Icon = step.icon
            const imageFirst = index % 2 === 0
            return (
              <article
                key={step.number}
                className="grid items-center gap-8 lg:grid-cols-[minmax(260px,360px)_1fr] lg:gap-14"
              >
                <div className={imageFirst ? 'lg:order-1' : 'lg:order-2'}>
                  <div className="mx-auto w-full max-w-[330px] rounded-[2.6rem] border-[9px] border-[#18231f] bg-[#18231f] p-[3px] shadow-2xl shadow-primary/15">
                    <div className="overflow-hidden rounded-[2rem] bg-background">
                      <img
                        src={step.image}
                        alt={step.alt}
                        loading={index === 0 ? 'eager' : 'lazy'}
                        className="h-auto w-full"
                        width={1080}
                        height={2400}
                      />
                    </div>
                  </div>
                  <p className="mt-3 text-center font-mono text-[11px] text-muted-foreground">
                    {version ? `Android ${version}` : 'Android'} · {step.label}
                  </p>
                </div>

                <div className={imageFirst ? 'lg:order-2' : 'lg:order-1'}>
                  <span className="flex items-center gap-2 font-mono text-xs font-semibold text-primary">
                    <Icon className="h-4 w-4" />
                    STEP {step.number}
                  </span>
                  <h3 className="mt-3 text-balance text-2xl font-bold tracking-tight sm:text-3xl">
                    {step.title}
                  </h3>
                  <div className="mt-4 flex flex-col gap-3 leading-relaxed text-muted-foreground">
                    {step.body.map((paragraph) => (
                      <p key={paragraph}>{paragraph}</p>
                    ))}
                  </div>
                  <ul className="mt-5 flex flex-col gap-2.5 rounded-xl border border-primary/20 bg-primary/5 p-4">
                    {step.points.map((point) => (
                      <li key={point} className="flex items-start gap-3 text-sm">
                        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </article>
            )
          })}
        </div>

        <div className="mt-16 rounded-2xl border border-amber-500/30 bg-amber-500/5 p-6">
          <h3 className="font-semibold text-foreground">Before your first write</h3>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            Always read the radio over the current Bluetooth or USB connection first. NewChirp keeps
            the verified image tied to that connection and selected model, so the write button stays
            locked when the backup does not match the radio in front of you.
          </p>
        </div>
      </div>
    </section>
  )
}
